/**
 * Monitoring Page — person selector for monitors and clinicians.
 *
 * Tapping a card selects that person as the active monitored user
 * and navigates to the Dashboard so every page shows their data.
 * Works identically for monitors and clinicians.
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Router } from "@vaadin/router";
import {
  relationshipService,
  type MonitoredUser,
} from "../services/relationships";
import { authService } from "../services/auth";
import {
  monitoredUserSelectionService,
  type SelectedMonitoredUser,
} from "../services/monitored-user-selection";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/badge/badge.js";
import "../components/app-shell";

@customElement("monitoring-page")
export class MonitoringPage extends LitElement {
  @state() private monitoredUsers: MonitoredUser[] = [];
  @state() private isLoading = true;
  @state() private error = "";
  @state() private loadingUserId: string | null = null;
  @state() private selectedUserId: string | null = null;

  static styles = css`
    :host {
      display: block;
    }

    .page-content {
      padding: 1rem;
      max-width: 1200px;
      margin: 0 auto;
    }

    h1 {
      margin: 0 0 1.5rem 0;
      font-size: 1.5rem;
      color: #333;
    }

    .subtitle {
      color: #6c757d;
      font-size: 0.875rem;
      margin-top: -1rem;
      margin-bottom: 1.5rem;
    }

    .users-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1rem;
    }

    .user-card {
      background: #f8f9fa;
      border-radius: 0.75rem;
      padding: 1.25rem;
      cursor: pointer;
      transition: all 0.2s ease;
      border: 2px solid transparent;
    }

    .user-card:hover {
      border-color: #007bff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .user-card.selected {
      border-color: var(--color-accent, #bd64fc);
      background: var(--color-accent-soft, #faf6ff);
    }

    .user-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 0.75rem;
    }

    .user-name {
      font-weight: 600;
      font-size: 1.125rem;
      color: #333;
    }

    .user-email {
      font-size: 0.75rem;
      color: #6c757d;
      margin-top: 0.25rem;
    }

    .user-stats {
      display: flex;
      gap: 1rem;
      margin-top: 1rem;
      padding-top: 0.75rem;
      border-top: 1px solid #e9ecef;
    }

    .stat {
      text-align: center;
    }

    .stat-value {
      font-size: 1.25rem;
      font-weight: 600;
      color: #333;
    }

    .stat-label {
      font-size: 0.75rem;
      color: #6c757d;
    }

    .last-sync {
      font-size: 0.75rem;
      color: #6c757d;
      margin-top: 0.75rem;
    }

    .empty-state {
      text-align: center;
      padding: 3rem;
      color: #6c757d;
    }

    .empty-state wa-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
      color: #dee2e6;
    }

    .spinner-container {
      display: flex;
      justify-content: center;
      padding: 3rem;
    }

    .stop-monitoring-btn {
      margin-top: 0.5rem;
    }

    .selected-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.7rem;
      font-weight: 600;
      color: var(--color-accent, #bd64fc);
      padding: 0.15rem 0.5rem;
      background: var(--color-accent-soft, #faf6ff);
      border-radius: 999px;
      border: 1px solid var(--color-accent, #bd64fc);
    }
  `;

  private _selectionUnsubscribe?: () => void;

  connectedCallback() {
    super.connectedCallback();
    this.selectedUserId = monitoredUserSelectionService.selected?.userId ?? null;
    this._selectionUnsubscribe = monitoredUserSelectionService.subscribe(
      (sel) => {
        this.selectedUserId = sel?.userId ?? null;
      },
    );
    this.loadMonitoredUsers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._selectionUnsubscribe?.();
  }

  private async loadMonitoredUsers() {
    this.isLoading = true;
    this.error = "";

    try {
      this.monitoredUsers = await relationshipService.getMonitoredUsers();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to load monitored users";
    } finally {
      this.isLoading = false;
    }
  }

  render() {
    const authState = authService.state;
    const isClinician = authState.roles?.includes("clinician");

    const pageTitle = isClinician ? "My Patients" : "People";
    const heading = isClinician
      ? "Who would you like to view?"
      : "Who would you like to view?";
    const subtitle =
      "Select a person to view their Dashboard, Today, and History.";

    return html`
      <app-shell pageTitle=${pageTitle}>
        <div class="page-content">
          <h1>${heading}</h1>
          <p class="subtitle">${subtitle}</p>

          ${this.error
            ? html`<wa-callout variant="danger" open>${this.error}</wa-callout>`
            : ""}
          ${this.isLoading
            ? this.renderLoading()
            : this.monitoredUsers.length === 0
              ? this.renderEmptyState()
              : this.renderUsersGrid()}
        </div>
      </app-shell>
    `;
  }

  private renderLoading() {
    return html`
      <div class="spinner-container">
        <wa-spinner style="font-size: 2rem;"></wa-spinner>
      </div>
    `;
  }

  private renderEmptyState() {
    const isClinician = authService.state.roles?.includes("clinician");
    return html`
      <div class="empty-state">
        <wa-icon name="users"></wa-icon>
        <h3>No one to monitor yet</h3>
        <p>
          ${isClinician
            ? "When patients share their data with you, they'll appear here."
            : "When friends or family share their data with you, they'll appear here."}
        </p>
        <p>Ask them to send you an invite link from their Share page.</p>
      </div>
    `;
  }

  private renderUsersGrid() {
    return html`
      <div class="users-grid">
        ${this.monitoredUsers.map((user) => this.renderUserCard(user))}
      </div>
    `;
  }

  private renderUserCard(user: MonitoredUser) {
    const lastSync = user.lastSyncedAt
      ? new Date(user.lastSyncedAt).toLocaleString()
      : "Never";
    const isLoading = this.loadingUserId === user.userId;
    const isSelected = this.selectedUserId === user.userId;

    return html`
      <div
        class="user-card ${isSelected ? "selected" : ""}"
        @click=${() => this._handleCardClick(user)}
      >
        <div class="user-header">
          <div>
            <div class="user-name">${user.displayName}</div>
            ${user.email
              ? html`<div class="user-email">${user.email}</div>`
              : ""}
          </div>
          ${isSelected
            ? html`<span class="selected-badge">
                <wa-icon name="circle-check" style="font-size:0.85rem"></wa-icon>
                Selected
              </span>`
            : html`<wa-badge
                variant="${user.relationshipType === "monitor"
                  ? "brand"
                  : "neutral"}"
              >
                ${user.relationshipType === "monitor"
                  ? "Family/Friend"
                  : "Patient"}
              </wa-badge>`}
        </div>

        ${user.lastGlucose
          ? html`
              <div class="user-stats">
                <div class="stat">
                  <div class="stat-value">${user.lastGlucose}</div>
                  <div class="stat-label">Last Glucose</div>
                </div>
                <div class="stat">
                  <div class="stat-value">${user.entriesCount || 0}</div>
                  <div class="stat-label">Entries</div>
                </div>
              </div>
            `
          : html`
              <div class="last-sync">
                ${isLoading
                  ? html`<wa-spinner></wa-spinner> Loading...`
                  : "Click to view data"}
              </div>
            `}

        <div class="last-sync">Last viewed: ${lastSync}</div>

        <wa-button
          class="stop-monitoring-btn"
          size="small"
          variant="text"
          @click=${(e: Event) => this.stopMonitoring(e, user)}
        >
          <wa-icon slot="prefix" name="circle-xmark"></wa-icon>
          Stop Monitoring
        </wa-button>
      </div>
    `;
  }

  private _handleCardClick(user: MonitoredUser) {
    // Select this person and navigate to dashboard
    const sel: SelectedMonitoredUser = {
      userId: user.userId,
      displayName: user.displayName,
      email: user.email,
      relationshipType: user.relationshipType,
    };
    monitoredUserSelectionService.select(sel);
    Router.go("/dashboard");
  }

  private async stopMonitoring(e: Event, user: MonitoredUser) {
    e.stopPropagation(); // Prevent card click

    if (
      !confirm(
        `Stop monitoring ${user.displayName}? You will no longer be able to view their data.`,
      )
    ) {
      return;
    }

    try {
      await relationshipService.stopMonitoring(user.userId);
      // If we were viewing this person, deselect
      if (this.selectedUserId === user.userId) {
        monitoredUserSelectionService.select(null);
      }
      await this.loadMonitoredUsers();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to stop monitoring";
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "monitoring-page": MonitoringPage;
  }
}
